import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDependency } from 'ditox-react';
import { produce } from 'immer';
import { clamp, head, last } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import useSessionSettings, { generateSessionSettingsQueryOptions } from '@/hooks/sessions/use-session-settings';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import { SendMessageCommand } from '@/models/messages/send-message';
import type { RespondedType, ResponseError } from '@/models/response';
import { getUserId } from '@/stores/auth';
import { addToast } from '@/stores/toast';
import { getDurationFormatKey } from '@/utils/datetime';
import { fetcher } from '@/utils/fetcher';
import { translateComplexKey } from '@/utils/translate';

export const AUTO_DELETE_TIMES = [
  30,
  60,
  90,
  120,
  300,
  10 * 60,
  30 * 60,
  60 * 60,
  3 * 60 * 60,
  6 * 60 * 60,
  12 * 60 * 60,
  24 * 60 * 60,
  3 * 24 * 60 * 60,
  7 * 24 * 60 * 60,
] as readonly number[];

export default function useUpdateSessionReadDelete(sessionId: number | null | undefined) {
  const { t } = useTranslation();
  const webSocketManager = useDependency(WEB_SOCKET_MANAGER_TOKEN);
  const queryClient = useQueryClient();
  const { setting } = useSessionSettings('session', sessionId);

  const mutation = useMutation<RespondedType, ResponseError, UpdateSessionTopRequest>({
    mutationKey: ['/UpdateChatSession', sessionId],
    mutationFn: (params) =>
      fetcher('/UpdateChatSession', { ...params, ChatSessionID: sessionId }, { validResponseCodes: [0] }),
    onSuccess: (_, params) => {
      if (sessionId) {
        queryClient.setQueryData(generateSessionSettingsQueryOptions('session', sessionId).queryKey, (state) =>
          produce(state, (draft) => {
            if (!draft) return;

            draft.ReadDelete = params.ReadDelete;
            draft.AutoDeleteTime = params.AutoDeleteTime;
          }),
        );
        const userId = getUserId() ?? 0;
        webSocketManager.dispatch({
          action: 'send',
          params: {
            message: {
              UserID: userId,
              Cmd: SendMessageCommand.Chat,
              // NOTE: this should use message key in the future
              Message:
                params.AutoDeleteTime > 0
                  ? t('conversation.setting.set-vanish-time', {
                      time: translateComplexKey(getDurationFormatKey(params.AutoDeleteTime), t),
                    })
                  : t('conversation.setting.disable-vanish'),
              ChatSessionID: sessionId,
              TimeStamp: Date.now(),
              MsgType: ConversationMessageContentType.Notice,
            },
          },
        });
      }
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });

  const setReadDelete = async (value: boolean) => {
    if (value === setting?.ReadDelete) return;

    const duration = value ? (head(AUTO_DELETE_TIMES) as number) : 0;

    await mutation.mutateAsync({
      ReadDelete: duration > 0,
      AutoDeleteTime: duration,
    });
  };

  const setReadDeleteDuration = async (autoDeleteTime: number) => {
    const duration =
      autoDeleteTime > 0
        ? clamp(autoDeleteTime, head(AUTO_DELETE_TIMES) as number, last(AUTO_DELETE_TIMES) as number)
        : autoDeleteTime;

    if (duration === setting?.AutoDeleteTime) return;

    await mutation.mutateAsync({
      ReadDelete: duration > 0,
      AutoDeleteTime: duration,
    });
  };

  return {
    setReadDelete,
    setReadDeleteDuration,
    ...mutation,
  };
}

type UpdateSessionTopRequest = {
  ReadDelete: boolean;
  AutoDeleteTime: number;
};
